import React, { FC } from 'react';
import {
  createStyles,
  makeStyles,
  styled,
  Theme,
} from '@material-ui/core/styles';
import Close from '@material-ui/icons/Close';
import Modal from '@material-ui/core/Modal';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';

import colors from '../../../../../themes/main-colors';
import { useTranslation } from '../../../../../hooks/useTranslation';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  Paper,
  Switch,
} from '@material-ui/core';
import PGEButton from '../../../../buttons';
import { Shadow } from '../../../../../constants';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import useAuth from '../../../../../hooks/useAuth';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modalWrapper: {
      top: '50%',
      left: '50%',
      width: '908px',
      display: 'flex',
      padding: theme.spacing(3.5, 3, 0.25, 3),
      overflow: 'auto',
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
      maxHeight: '77.21vh',
      borderRadius: '5px',
      flexDirection: 'column',
      backgroundColor: colors.modalBackgroundColor,
      [theme.breakpoints.down('sm')]: {
        top: '50%',
        width: '90%',
        maxHeight: '100%',
        padding: theme.spacing(3),
      },
    },
    closeButton: {
      position: 'absolute',
      top: 6,
      right: 24,
    },

    resettingBreakersDiv: {
      background: colors.navBarBackground,
      paddingLeft: '1rem',
      paddingRight: '1rem',
      paddingBottom: '1rem',
      boxShadow: Shadow.ONE,
      maxHeight: '48vh',
      overflow: 'auto',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    circuitBreakerAccordion: {
      background: colors.lightGray6,
      padding: '0.5rem',
      boxShadow: Shadow.NONE,
    },
    accordionDetails: {
      display: 'flex',
      flexDirection: 'column',
    },
    title: {
      lineHeight: '2.25rem',
    },

    whiteDivTextStyleDetails: {
      paddingBottom: '1rem',
    },
    textToBold: {
      fontWeight: 'bold',
    },

    buttonContainer: {
      display: 'flex',
      padding: '1rem',
      width: '100%',
      justifyContent: 'space-evenly',
    },
    gridDetails: {
      boxShadow: Shadow.NONE,
      background: colors.lightGray6,
    },

    toggleButton: {
      '& .MuiSwitch-root': {
        width: '60px',
        height: '39px',
      },
    },
    dividerDetails: {
      padding: '1px',
      backgroundColor: colors.white,
    },
    grid2Details: {
      marginTop: '2px',
    },
    switchDivDetails: {
      height: '7vh',
      backgroundColor: colors.lightGray6,
    },
    switchDetails: {
      display: 'flex',
      justifyContent: 'space-evenly',
    },
    reportOutageButton: {
      borderStyle: 'solid',
      borderColor: colors.sparkBlue,
      backgroundColor: colors.white,
      borderRadius: '5px',
    },
    reportOutageMsg: {
      color: colors.sparkBlue,
    },
    textMarginBottom: {
      marginBottom: '10px',
    },
    resetButton: {
      padding: '8px 16px',
    },
    backButton: {
      flex: 1,
    },
    subButtonContainer: {
      display: 'flex',
      justifyContent: 'space-around',
      flex: 2,
    },
    switchDivDetails2: {
      marginTop: '10px',
      display: 'flex',
      margin: '18px',
      justifyContent: 'space-between',
      paddingTop: '15px',
    },

    checkBreakerBackButton: {
      color: colors.sparkBlue,
    },

    checkBreakerContent1: {
      marginTop: '0',
    },

    checkBreakerHeader: {
      marginLeft: '1rem',
      marginBottom: '1rem',
    },

    modalBody: {},
    kickerText: {
      paddingBottom: theme.spacing(1.875),
      paddingTop: theme.spacing(1.25),
      fontSize: '1.25rem',
      lineHeight: '1.5rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
      },
    },
  }),
);

type Props = {
  open: boolean;
  onClose: () => void;
  onProceed: () => void;
  onReset: () => void;
  onPrevious: () => void;
};

const Item = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
}));

const OutageCheckBreakersPopup: FC<Props> = ({
  open,
  onClose,
  onProceed,
  onReset,
  onPrevious,
}: Props) => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  useAuth();

  const OutageToggleSwitch = styled(Switch)(({ theme }) => ({
    width: 30,
    height: 18,
    padding: 0,
    margin: '5px 14px',
    display: 'flex',
    '&:active': {
      '& .MuiSwitch-thumb': {
        width: 15,
      },
      '& .MuiSwitch-switchBase.Mui-checked': {
        transform: 'translateX(9px)',
      },
    },
    '& .MuiSwitch-switchBase': {
      padding: 2,
      '&.Mui-checked': {
        transform: 'translateX(12px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: 'white',
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
      width: 14,
      height: 14,
      borderRadius: 6,
      backgroundColor: colors.sparkBlue,
      transition: theme.transitions.create(['width'], {
        duration: 200,
      }),
    },
    '& .MuiSwitch-track': {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: 'white',
      boxSizing: 'border-box',
      border: '1px solid gray',
    },
  }));
  const customerCarePortland = t('CUSTOMER_CARE_NUMBER_PORTLAND');
  const customerCareNumber = t('CUSTOMER_CARE_NUMBER_OTHER');

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      <div className={classes.modalWrapper}>
        <div className={classes.closeButton}>
          <IconButton
            aria-label="close"
            color="inherit"
            onClick={onClose}
            data-testid="close-icon"
          >
            <Close />
          </IconButton>
        </div>

        <Grid className={classes.modalBody}>
          <div>
            <Typography variant={'h3'} className={classes.checkBreakerHeader}>
              {t('BEFORE_OUTAGE_CHECK_BREAKERS_FIRST')}
            </Typography>
          </div>

          <div className={classes.resettingBreakersDiv}>
            <div className={classes.whiteDivTextStyleDetails}>
              <Typography
                variant={'subtitle2'}
                className={classes.checkBreakerContent1}
              >
                {richT('SOMETIMES_RESTORING_POWER_CAN_BE_SIMPLE')}
              </Typography>
              <Typography
                variant={'subtitle1'}
                className={classes.textMarginBottom}
              >
                {t('IF_YOU_ARE_UNSURE_OF_RESETTING')}
              </Typography>
              <Typography
                variant={'subtitle1'}
                className={classes.textMarginBottom}
              >
                {t('UNABLE_TO_LOCATE_BREAKER_OR_MAIN_BREAKER')}{' '}
                {
                  <a
                    href={'tel:' + customerCarePortland}
                    style={{
                      textDecoration: 'underline',
                      color: colors.brightBlue,
                    }}
                  >
                    {' '}
                    {customerCarePortland}
                  </a>
                }{' '}
                {t('OR')}{' '}
                {
                  <a
                    href={'tel:' + customerCareNumber}
                    style={{
                      textDecoration: 'underline',
                      color: colors.brightBlue,
                    }}
                  >
                    {' '}
                    {customerCareNumber}
                  </a>
                }{' '}
                {t('TO_SPEAK_WITH_AN_ADVISOR')}
              </Typography>
            </div>

            <div>
              <Accordion className={classes.circuitBreakerAccordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant={'subtitle2'}>
                    {t('HOW_TO_FIND_YOUR_BREAKER')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <Typography
                    variant={'subtitle1'}
                    className={classes.textMarginBottom}
                  >
                    {richT('HOW_TO_FIND_BREAKER_CONTENT')}
                  </Typography>
                  <Typography
                    variant={'subtitle2'}
                    className={classes.textMarginBottom}
                  >
                    {t('LOCATING_YOUR_BREAKER_BOX')}
                  </Typography>
                  <Divider
                    variant="middle"
                    className={classes.dividerDetails}
                  />
                  <Grid container spacing={2} className={classes.grid2Details}>
                    <Grid xs={4}>
                      <Item className={classes.gridDetails}>
                        <img
                          style={{
                            maxWidth: '80%',
                            maxHeight: '80%',
                            paddingTop: '10px',
                          }}
                          src={require('../../../../../images/InsideBreaker.png')}
                          alt="Check Inside Breaker Details"
                        />
                      </Item>
                    </Grid>

                    <Grid xs={8}>
                      <Item className={classes.gridDetails}>
                        <Typography variant={'subtitle2'}>
                          {t('IF_YOU_DONT_HAVE_OUTSIDE_BREAKER')}
                        </Typography>
                        <Typography variant={'subtitle2'}>
                          {t('STEPS_TO_RESET_INSIDE_BREAKERS')}
                        </Typography>
                        <ol>
                          <li className={classes.textToBold}>
                            <Typography variant={'subtitle1'}>
                              {t('TURN_OFF_THE_MAIN_BREAKER')}
                            </Typography>
                          </li>
                          <li className={classes.textToBold}>
                            <Typography variant={'subtitle1'}>
                              {t('TURN_OFF_THE_SMALLER_BREAKERS')}
                            </Typography>
                          </li>
                          <li className={classes.textToBold}>
                            <Typography variant={'subtitle1'}>
                              {t('TURN_ON_THE_MAIN_BREAKER')}
                            </Typography>
                          </li>
                          <li className={classes.textToBold}>
                            <Typography variant={'subtitle1'}>
                              {t('TURN_ON_THE_SMALLER_BREAKERS')}
                            </Typography>
                          </li>
                        </ol>
                      </Item>
                    </Grid>
                  </Grid>

                  <Divider
                    variant="middle"
                    className={classes.dividerDetails}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Item className={classes.gridDetails}>
                        <img
                          className=""
                          style={{
                            maxWidth: '80%',
                            maxHeight: '80%',
                            paddingTop: '10px',
                          }}
                          src={require('../../../../../images/OutsideBreaker.png')}
                          alt="Check Outside Breaker Details"
                        />
                      </Item>
                    </Grid>

                    <Grid item xs={8}>
                      <Item className={classes.gridDetails}>
                        <Typography variant={'subtitle2'}>
                          {t('OUTSIDE_BREAKER')}
                        </Typography>
                        <Typography variant={'subtitle1'}>
                          {t('OUTSIDE_BREAKER_PARAH1')}
                        </Typography>
                        <Typography variant={'subtitle2'}>
                          {t('STEPS_TO_RESET_OUTSIDE_BREAKERS')}
                        </Typography>
                        <ol>
                          <li className={classes.textToBold}>
                            <Typography variant={'subtitle1'}>
                              {t('LOCATE_THE_OUTSIDE_BREAKER')}
                            </Typography>
                          </li>
                          <li className={classes.textToBold}>
                            <Typography variant={'subtitle1'}>
                              {t('ATTEMPT_TO_RESET_THE_OUTSIDE_BREAKER')}
                            </Typography>
                          </li>
                        </ol>
                      </Item>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </div>

            <div className={classes.switchDivDetails}>
              <div className={classes.switchDivDetails2}>
                <Typography variant={'subtitle2'}>
                  {t('WERE_YOU_ABLE_TO_RESET_YOUR_BREAKER')}
                </Typography>
                <div className={classes.switchDetails}>
                  <Typography variant={'subtitle2'}>{t('NO')}</Typography>{' '}
                  <OutageToggleSwitch
                    inputProps={{ 'aria-label': 'Outage Toggle Switch' }}
                  />
                  <Typography variant={'subtitle2'}>
                    {t('SWITCH_YES')}
                  </Typography>{' '}
                </div>
              </div>
            </div>
          </div>
          <div className={classes.buttonContainer}>
            <div className={classes.backButton}>
              <Button
                className={classes.checkBreakerBackButton}
                onClick={() => {
                  onPrevious();
                }}
                style={{
                  textDecoration: 'underline',
                }}
              >
                {t('BACK')}
              </Button>
            </div>
            <div className={classes.subButtonContainer}>
              <div className={classes.reportOutageButton}>
                <Button className={classes.reportOutageMsg} onClick={onProceed}>
                  {t('THIS_DID_NOT_WORK_REPORT_OUTAGE')}
                </Button>
              </div>
              <div>
                <PGEButton onClick={onReset} className={classes.resetButton}>
                  {t('RESETTING_THE_BREAKER_WORKED')}
                </PGEButton>
              </div>
            </div>
          </div>
        </Grid>
      </div>
    </Modal>
  );
};

export { OutageCheckBreakersPopup };
